import React, { useState, useEffect, memo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyStore } from '../store/storeSlice';
import { fetchMerchantNotifications } from '../notifications/notificationsSlice';
import RoleGuard from '../roles/RoleGuard';
import FeatureGate from '../../components/FeatureGate';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import KYCOnboardingModal from '../../components/KYCOnboardingModal';
import PostLoginActionsModal from '../../components/PostLoginActionsModal';
import KYCBadge from '../../components/KYCBadge';
import ImpersonationBanner from '../../components/ImpersonationBanner';
import TrialBanner from '../../components/TrialBanner';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useIsInsideShell, ShellProvider } from '../../contexts/ShellContext';
import axiosInstance from '../../axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';
import { QRSidebarWidget } from '../../components/QRCode';
import MobileBottomNav from '../../components/MobileBottomNav';
import AIChatLauncher from '../../components/ai/AIChatLauncher';
import LiveVisitorPopup from '../storefront-chat/LiveVisitorPopup';
// OnboardingTour removed - react-joyride causes sidebar layout issues
import './Shell.css';
import LocationSwitcher from './LocationSwitcher';
import LiveToggle from './LiveToggle';
import HeaderActions from './HeaderActions';
import ProfileMenu from './ProfileMenu';
// Lucide React icons for consistency
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Plus,
  Minus,
  Search,
  Filter,
  Zap,
  Mail,
  Building2,
  Home,
  TrendingUp,
  Image,
  FileText,
  Percent,
  RotateCcw,
  DollarSign,
  CreditCard,
  Banknote,
  Truck,
  MessageSquare,
  BotMessageSquare,
  Globe,
  Calendar,
  Clock,
  Star,
  Heart,
  MapPin,
  User,
  HelpCircle,
  ChevronDown,
  X,
  Edit,
  Trash2,
  Menu,
  MoreVertical,
  Copy,
  ExternalLink,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Grid3x3,
  Sparkles,
  Crown,
  Store,
  Receipt,
  Undo2,
  Megaphone,
  Wallet,
  Link2,
  UserPlus,
  Layers,
  Gift,
  MessagesSquare,
  Landmark,
  ClipboardList
} from 'lucide-react';

// Helper to format merchantRole for display
const formatRoleLabel = (merchantRole) => {
  if (!merchantRole) return 'Team Member';
  // Convert SNAKE_CASE or CAPS to Title Case
  return merchantRole
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Icon = ({ name, size = 20, className = '', style = {} }) => {
  // All icons inherit color from parent via currentColor.
  // strokeWidth 1.75 (vs lucide's default 2) gives the sidebar a lighter,
  // more modern weight and reduces the optical-density gap between simple
  // icons (Overview grid) and detailed ones (Settings gear, Marketing mail).
  const iconProps = { size, className, style, strokeWidth: 1.75 };
  const icons = {
    dashboard: <LayoutDashboard {...iconProps} />,
    products: <Package {...iconProps} />,
    inventory_report: <ClipboardList {...iconProps} />,
    orders: <ShoppingCart {...iconProps} />,
    pos: <Store {...iconProps} />,
    refunds: <RotateCcw {...iconProps} />,
    returns: <Undo2 {...iconProps} />,
    customers: <Users {...iconProps} />,
    analytics: <BarChart3 {...iconProps} />,
    ai: <Zap {...iconProps} />,
    marketing: <Mail {...iconProps} />,
    business: <Building2 {...iconProps} />,
    store: <Home {...iconProps} />,
    growth: <TrendingUp {...iconProps} />,
    chevron: <ChevronRight {...iconProps} />,
    plus: <Plus {...iconProps} />,
    minus: <Minus {...iconProps} />,
    media: <Image {...iconProps} />,
    pages: <FileText {...iconProps} />,
    campaigns: <Mail {...iconProps} />,
    email: <Mail {...iconProps} />,
    discounts: <Percent {...iconProps} />,
    abandoned: <RotateCcw {...iconProps} />,
    transactions: <DollarSign {...iconProps} />,
    expenses: <CreditCard {...iconProps} />,
    settlements: <Banknote {...iconProps} />,
    shipping: <Truck {...iconProps} />,
    whatsapp: (
      // WhatsApp brand glyph — filled so it stays recognizable at small sizes.
      // Inherits color via currentColor so active/inactive nav states still work.
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        style={style}
        aria-hidden
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
      </svg>
    ),
    sms: <MessageSquare {...iconProps} />,
    storefront_chat: <BotMessageSquare {...iconProps} />,
    team: <Users {...iconProps} />,
    activity: <Clock {...iconProps} />,
    apps: <Grid3x3 {...iconProps} />,
    payment: <CreditCard {...iconProps} />,
    globe: <Globe {...iconProps} />,
    external: <ExternalLink {...iconProps} />,
    calendar: <Calendar {...iconProps} />,
    bell: <Bell {...iconProps} />,
    settings: <Settings {...iconProps} />,
    marketplace: <Globe {...iconProps} />,
    upgrade: <Crown {...iconProps} />,
    sparkles: <Sparkles {...iconProps} />,
    map: <MapPin {...iconProps} />,
    billing: <Receipt {...iconProps} />,
    ad: <Megaphone {...iconProps} />,
    wallet: <Wallet {...iconProps} />,
    finance: <Landmark {...iconProps} />,
    deeplinks: <Link2 {...iconProps} />,
    affiliates: <UserPlus {...iconProps} />,
    referrals: <Gift {...iconProps} />,
    collections: <Layers {...iconProps} />,
    categories: <Grid3x3 {...iconProps} />,
    businesscard: <CreditCard {...iconProps} />,
    community: <MessagesSquare {...iconProps} />
  };

  return icons[name] || null;
};

/**
 * Internal sidebar NavLink. Optionally accepts a `featureId` — when provided
 * AND the merchant lacks access, the link renders as a locked item with a
 * small "PRO" badge. Click opens the pricing modal instead of navigating.
 *
 * This replaces the earlier `<FeatureGate silent>` pattern around NavLinks
 * (which hid the item entirely). Showing locked items with PRO badge is the
 * Salesive pattern and converts upgrades better than hiding.
 */
const NavLink = ({ to, icon, children, active, external = false, onClick, isCollapsed = false, featureId, badge }) => {
  const navigate = useNavigate();
  // Hooks must be called unconditionally — when featureId is undefined the
  // hook returns hasAccess=true quickly and the locked branch is skipped.
  const { hasAccess, loading } = useFeatureAccess(featureId);

  const isLocked = featureId && !loading && !hasAccess;

  if (isLocked) {
    return (
      <button
        type="button"
        className={`nav-link nav-link-locked ${isCollapsed ? 'collapsed' : ''}`}
        style={{ margin: '4px 12px' }}
        onClick={(e) => {
          e.preventDefault();
          navigateToUpgrade(navigate);
          if (onClick) onClick();
        }}
        title={isCollapsed ? `${children} — upgrade to PRO` : `Upgrade to PRO to unlock ${children}`}
        aria-label={`${children} — locked, upgrade to PRO`}
      >
        <Icon name={icon} size={20} />
        <span className="nav-link-text">{children}</span>
        {!isCollapsed && (
          <span className="nav-link-pro-badge" aria-hidden="true">PRO</span>
        )}
      </button>
    );
  }

  return (
    <Link
      to={to}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={`nav-link ${active ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      style={{
        margin: '4px 12px'
      }}
      onClick={onClick}
      title={isCollapsed ? children : undefined}
    >
      <Icon name={icon} size={20} />
      <span className="nav-link-text">{children}</span>
      {badge && !isCollapsed && (
        <span className="nav-link-badge" aria-hidden="true">{badge}</span>
      )}
      {external && !isCollapsed && (
        <Icon name="external" size={14} style={{ marginLeft: 'auto', opacity: 0.7 }} />
      )}
    </Link>
  );
};

const SectionHeader = ({ children, isCollapsed = false }) => (
  <div className={`section-header ${isCollapsed ? 'collapsed' : ''}`}>
    {!isCollapsed && children}
  </div>
);

/**
 * Expandable sidebar section. Optionally accepts a `featureId` — when the
 * merchant lacks access, the section header shows a "PRO" badge and clicking
 * opens the pricing modal instead of expanding. The items inside don't
 * render in the locked case (no point — they'd all be locked anyway).
 */
const ExpandableSection = ({ title, icon, children, isExpanded, onToggle, isCollapsed = false, featureId }) => {
  const navigate = useNavigate();
  const { hasAccess, loading } = useFeatureAccess(featureId);

  const isLocked = featureId && !loading && !hasAccess;

  return (
    <div className={`expandable-section ${isCollapsed ? 'collapsed' : ''}`}>
      <div
        onClick={() => {
          if (isLocked) {
            navigateToUpgrade(navigate);
            return;
          }
          onToggle();
        }}
        className={`expandable-section-trigger ${isExpanded && !isLocked ? 'expanded' : ''} ${isCollapsed ? 'collapsed' : ''} ${isLocked ? 'locked' : ''}`}
        title={isCollapsed ? title : (isLocked ? `${title} — upgrade to PRO` : undefined)}
        role="button"
        tabIndex={0}
      >
        <Icon name={icon} size={20} className="icon" />
        <span className="section-title">{title}</span>
        {!isCollapsed && isLocked && (
          <span className="nav-link-pro-badge" aria-hidden="true">PRO</span>
        )}
        {!isCollapsed && !isLocked && (
          <ChevronDown
            size={16}
            className={`chevron ${isExpanded ? 'expanded' : ''}`}
            strokeWidth={2}
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}
          />
        )}
      </div>
      {isExpanded && !isCollapsed && !isLocked && (
        <div className="expandable-section-content">
          {children}
        </div>
      )}
    </div>
  );
};

// Memoized BrandHeader component to prevent logo blinking on navigation
const BrandHeader = memo(function BrandHeader({ isCollapsed = false, onToggleCollapse }) {
  return (
    <div className={`brand-header ${isCollapsed ? 'collapsed' : ''}`}>
      <img
        src="/revstar-logo.png"
        alt="Revstar"
        className="brand-logo"
      />
      <span className="brand-text">Revstar</span>
      {/* Collapse Toggle Button - Integrated into header */}
      <button
        onClick={onToggleCollapse}
        className="sidebar-collapse-toggle"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </div>
  );
});

export default function Shell({ children }){
  // Check if we're already inside a Shell - prevents double rendering
  const isInsideShell = useIsInsideShell();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useSelector(s => s.store.current);
  const location = useLocation();
  const { isAuthenticated, user, logout: secureLogout } = useSecureAuth();
  // Notifications list/count are read inside <HeaderActions /> directly.

  // All useState hooks must be called before any early return
  // Live/offline state and its toggle handler now live inside <LiveToggle />
  // (reads from Redux directly, single source of truth).
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  // Location state lives in LocationSwitcher — see header-left below.
  // Custom-domain / storefront URL state lives in HeaderActions.
  const [expandedSections, setExpandedSections] = useState({
    manageStore: false,
    sales: false,
    pos: false,
    financials: false,
    marketing: false,
    marketplace: false,
    content: false,
    settings: false,
    billing: false
  });

  // Profile chip popover state. Opens upward (top of sidebar footer) on
  // click; closes on outside click or selecting an item.
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenuOpen]);

  // Sidebar collapsed state with localStorage persistence
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Only check localStorage on desktop (window width > 768px)
    if (typeof window !== 'undefined' && window.innerWidth > 768) {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved === 'true';
    }
    return false;
  });

  // Marketplace NEW badge — visible for the first 30 days after each
  // merchant's first sidebar render, then auto-hides.
  const [showMarketplaceNewBadge, setShowMarketplaceNewBadge] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const KEY = 'revstarMarketplaceBadgeFirstSeenAt';
      const stored = localStorage.getItem(KEY);
      const now = Date.now();
      if (!stored) {
        localStorage.setItem(KEY, String(now));
        return true;
      }
      const firstSeenAt = Number(stored);
      if (!Number.isFinite(firstSeenAt)) {
        localStorage.setItem(KEY, String(now));
        return true;
      }
      const daysElapsed = (now - firstSeenAt) / (1000 * 60 * 60 * 24);
      return daysElapsed < 30;
    } catch {
      return true;
    }
  });

  // All useEffect hooks must be called before any early return
  useEffect(() => {
    if (isAuthenticated && !isInsideShell) {
      dispatch(fetchMyStore());
      dispatch(fetchMerchantNotifications());

      // Check email verification status and redirect if blocked
      const checkEmailVerification = async () => {
        try {
          const response = await axiosInstance.get('/merchant/auth/verification-status');
          if (response.data?.success && response.data.isBlocked) {
            // Redirect to verification required page
            navigate('/verification-required');
          }
        } catch (error) {
          // Silently ignore - don't block dashboard access if check fails
          console.error('Error checking email verification:', error);
        }
      };
      checkEmailVerification();

      // Custom domains are now fetched inside <HeaderActions /> for the
      // storefront link. Locations are owned by <LocationSwitcher /> — it
      // fetches its own data, persists selection to localStorage, and
      // dispatches a `locationChanged` window event when the merchant
      // switches. Shell no longer holds location state.

      // Fetch active announcement
      const fetchAnnouncement = async () => {
        try {
          const response = await axiosInstance.get('/merchant/announcements/active');
          if (response.data?.announcement) {
            const ann = response.data.announcement;
            // Check if dismissed in localStorage (24h cache)
            const dismissedKey = `ann_dismissed_${ann.id}`;
            const dismissedAt = localStorage.getItem(dismissedKey);
            if (!dismissedAt || Date.now() - parseInt(dismissedAt) > 24 * 60 * 60 * 1000) {
              setAnnouncement(ann);
            }
          }
        } catch (error) {
          // 404 is expected if no active announcement
          if (error.response?.status !== 404) {
            console.error('Error fetching announcement:', error);
          }
        }
      };
      fetchAnnouncement();
    }
  }, [isAuthenticated, isInsideShell, dispatch]);

  // Check for impersonation from user object (included in JWT token)
  useEffect(() => {
    if (user?.impersonation) {
      setImpersonationData(user.impersonation);
      console.log('✅ [Impersonation] Active impersonation session detected:', user.impersonation);
    } else {
      setImpersonationData(null);
    }
  }, [user]);

  // Early return AFTER all hooks - if already inside Shell, just render children
  if (isInsideShell) {
    return <>{children}</>;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Toggle sidebar collapsed state
  const toggleSidebarCollapse = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await secureLogout();
    // SecureAuth logout already redirects to /login
  };

  const handleExitImpersonation = () => {
    console.log('🔄 [Impersonation] Exiting impersonation session...');

    // Navigate directly to the backend endpoint
    // The backend will clear the merchant cookie, set the admin cookie, and redirect to admin console
    const backendUrl = process.env.REACT_APP_BACKEND_URL ||
      (window.location.hostname === 'localhost' ? 'http://localhost:4000' : window.location.origin);

    // Use window.location to navigate so the browser follows the backend's redirect
    // This ensures cookies are set/cleared properly
    window.location.href = `${backendUrl}/api/admin/exit-impersonation`;
  };

  return (
    <ShellProvider>
      <div className="shell-container">
        {/* Impersonation Banner */}
        {impersonationData && (
          <ImpersonationBanner
            impersonationData={impersonationData}
            onExit={handleExitImpersonation}
          />
        )}

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Logo - memoized to prevent blink on navigation */}
        <BrandHeader isCollapsed={isSidebarCollapsed} onToggleCollapse={toggleSidebarCollapse} />

        {/* Navigation */}
        <nav className="nav-section">
          {/* Main - Always visible. Overview is the new welcome/lifecycle
              landing surface; Dashboard is now KPIs-only. AI Analytics has
              been moved out of the top-level sidebar — it's surfaced via the
              floating sparkle button instead. */}
          {!isSidebarCollapsed && <div className="nav-group-label">Main</div>}

          <NavLink
            to="/overview"
            icon="dashboard"
            active={isActive('/overview')}
            onClick={() => setIsMobileMenuOpen(false)}
            isCollapsed={isSidebarCollapsed}
          >
            Overview
          </NavLink>

          <NavLink
            to="/dashboard"
            icon="growth"
            active={isActive('/dashboard')}
            onClick={() => setIsMobileMenuOpen(false)}
            isCollapsed={isSidebarCollapsed}
          >
            Dashboard
          </NavLink>

          <RoleGuard permission="analytics:view">
            <NavLink
              to="/analytics"
              icon="analytics"
              active={isActive('/analytics')}
              onClick={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
            >
              Analytics
            </NavLink>
          </RoleGuard>

          <NavLink
            to="/community"
            icon="community"
            active={isActive('/community')}
            onClick={() => setIsMobileMenuOpen(false)}
            isCollapsed={isSidebarCollapsed}
          >
            Community
          </NavLink>

          {!isSidebarCollapsed && <div className="nav-group-label">Manage</div>}

          {/* Manage Store Section - Expandable */}
          <ExpandableSection
            title="Manage Store"
            icon="store"
            isExpanded={expandedSections.manageStore}
            onToggle={() => toggleSection('manageStore')}
            isCollapsed={isSidebarCollapsed}
          >
            <RoleGuard permission="products:read">
              <NavLink
                to="/products"
                icon="products"
                active={isActive('/products')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Products
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="products:read">
              <NavLink
                to="/products/inventory-report"
                icon="inventory_report"
                active={isActive('/products/inventory-report')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Inventory Report
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="products:read">
              <NavLink
                to="/categories"
                icon="categories"
                active={isActive('/categories')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Categories
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="products:read">
              <NavLink
                to="/collections"
                icon="collections"
                active={isActive('/collections')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Collections
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="store:view">
              <NavLink
                to="/manage-store/information"
                icon="store"
                active={isActive('/manage-store/information')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Store Information
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="store:view">
              <NavLink
                to="/manage-store/storefront-settings"
                icon="settings"
                active={isActive('/manage-store/storefront-settings')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Storefront Settings
              </NavLink>
            </RoleGuard>
          </ExpandableSection>

          {/* Sales Section - Expandable */}
          <ExpandableSection
            title="Sales"
            icon="orders"
            isExpanded={expandedSections.sales}
            onToggle={() => toggleSection('sales')}
            isCollapsed={isSidebarCollapsed}
          >

            <RoleGuard permission="orders:read">
              <NavLink
                to="/orders"
                icon="orders"
                active={isActive('/orders')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Orders
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="customers:view">
              <NavLink
                to="/customers"
                icon="customers"
                active={isActive('/customers')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Customers
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="customers:view">
              <NavLink
                to="/tags-segments"
                icon="customers"
                active={isActive('/tags-segments')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.tags"
              >
                Tags & Segments
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="discounts:view">
              <NavLink
                to="/discounts"
                icon="discounts"
                active={isActive('/discounts')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.discounts"
              >
                Discounts
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="orders:read">
              <NavLink
                to="/appointments"
                icon="calendar"
                active={isActive('/appointments')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Appointments
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="orders:read">
              <NavLink
                to="/abandoned"
                icon="abandoned"
                active={isActive('/abandoned')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.abandoned_carts"
              >
                Abandoned Carts
              </NavLink>
            </RoleGuard>
          </ExpandableSection>

          {/* POS Section - Expandable. Feature-gated as a unit (sales.pos) —
              FREE merchants see it with a "PRO" badge and click opens the
              pricing modal instead of expanding. Merchants who dismissed
              POS at the discovery prompt also hide it here — the toggle
              in Settings > Business preferences flips it back. */}
          {store?.settings?.posOnboardingChoice !== 'dismissed' && (
          <ExpandableSection
            title="Point of Sale"
            icon="pos"
            isExpanded={expandedSections.pos}
            onToggle={() => toggleSection('pos')}
            isCollapsed={isSidebarCollapsed}
            featureId="sales.pos"
          >
              <RoleGuard permission="orders:edit">
                <NavLink
                  to="/pos"
                  icon="pos"
                  active={isActive('/pos')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isCollapsed={isSidebarCollapsed}
                >
                  POS Terminal
                </NavLink>
              </RoleGuard>

              <RoleGuard permission="pos:reports">
                <NavLink
                  to="/shift-analytics"
                  icon="analytics"
                  active={isActive('/shift-analytics')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isCollapsed={isSidebarCollapsed}
                >
                  Shift Analytics
                </NavLink>
              </RoleGuard>

              <RoleGuard permission="pos:reports">
                <NavLink
                  to="/pos/activity"
                  icon="activity"
                  active={isActive('/pos/activity')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isCollapsed={isSidebarCollapsed}
                >
                  POS Activity Logs
                </NavLink>
              </RoleGuard>

              {/* POS Service Charges retired — see /wallet, filter category POS_FEE_ACCRUAL */}
            </ExpandableSection>
          )}

          {/* Financials Section - Expandable */}
          <ExpandableSection
            title="Financials"
            icon="transactions"
            isExpanded={expandedSections.financials}
            onToggle={() => toggleSection('financials')}
            isCollapsed={isSidebarCollapsed}
          >
            {/* Settlements + Bank Transfers retired — consolidated into Wallet.
                Activity log filterable by Source (DVA / Paystack / Flutterwave) +
                Category (Customer payment / Platform fee / etc.). */}
            <RoleGuard permission="settlements:view">
              <NavLink
                to="/wallet"
                icon="wallet"
                active={isActive('/wallet')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Wallet
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="finance:view">
              <NavLink
                to="/finance"
                icon="finance"
                active={isActive('/finance')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                /* Passing featureId makes the NavLink show a "PRO" badge
                   + open the pricing modal on click when the merchant's
                   tier has finance.overview disabled, instead of letting
                   them click through to a fully-locked page. */
                featureId="finance.overview"
              >
                Finance
              </NavLink>
            </RoleGuard>

            {/* Billing group — collapses the two invoice surfaces under
                a single section so the sidebar makes the merchant<>
                schedule distinction obvious. The parent itself is
                gated by billing.recurring so FREE-tier sees one PRO
                badge on the section header (not two on the children).
                Children inherit the permission check via RoleGuard. */}
            <RoleGuard permission="orders:view">
              <ExpandableSection
                title="Billing"
                icon="billing"
                isExpanded={expandedSections.billing}
                onToggle={() => toggleSection('billing')}
                isCollapsed={isSidebarCollapsed}
                featureId="billing.recurring"
              >
                <NavLink
                  to="/billing/invoices"
                  icon="billing"
                  active={isActive('/billing/invoices')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isCollapsed={isSidebarCollapsed}
                >
                  Invoices
                </NavLink>
                <NavLink
                  to="/billing/recurring-invoices"
                  icon="billing"
                  active={isActive('/billing/recurring-invoices')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isCollapsed={isSidebarCollapsed}
                >
                  Recurring schedules
                </NavLink>
              </ExpandableSection>
            </RoleGuard>

            <RoleGuard permission="orders:read">
              <NavLink
                to="/refunds"
                icon="refunds"
                active={isActive('/refunds')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Refunds
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="orders:read">
              <NavLink
                to="/return-requests"
                icon="returns"
                active={isActive('/return-requests')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Return Requests
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="orders:read">
              <NavLink
                to="/expenses"
                icon="expenses"
                active={isActive('/expenses')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Expenses
              </NavLink>
            </RoleGuard>
          </ExpandableSection>

          {/* Marketing Section - Expandable */}
          <ExpandableSection
            title="Marketing"
            icon="marketing"
            isExpanded={expandedSections.marketing}
            onToggle={() => toggleSection('marketing')}
            isCollapsed={isSidebarCollapsed}
          >
            <RoleGuard permission="campaigns:view">
              <NavLink
                to="/email-campaigns"
                icon="email"
                active={isActive('/email-campaigns')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.email"
              >
                Email Campaigns
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="whatsapp:view">
              <NavLink
                to="/whatsapp"
                icon="whatsapp"
                active={isActive('/whatsapp') && !isActive('/whatsapp/launch-boost')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.whatsapp"
              >
                WhatsApp
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="whatsapp:view">
              <NavLink
                to="/whatsapp/launch-boost"
                icon="growth"
                active={isActive('/whatsapp/launch-boost')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.launch_boost"
              >
                Launch Boost
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="chat:view:own">
              <NavLink
                to="/storefront-chat"
                icon="storefront_chat"
                active={isActive('/storefront-chat')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="sales.storefront_chatbot"
              >
                Storefront chat
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="customers:view">
              <NavLink
                to="/sms"
                icon="sms"
                active={isActive('/sms')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="marketing.sms"
              >
                SMS
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="store:manage">
              <NavLink
                to="/ads"
                icon="ad"
                active={isActive('/ads')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Advertisement
              </NavLink>
            </RoleGuard>

            <NavLink
              to="/deep-links"
              icon="deeplinks"
              active={isActive('/deep-links')}
              onClick={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              featureId="marketing.deep_links"
            >
              Deep Links
            </NavLink>

            <NavLink
              to="/affiliates"
              icon="affiliates"
              active={isActive('/affiliates')}
              onClick={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              featureId="marketing.affiliates"
            >
              Affiliates
            </NavLink>

            <NavLink
              to="/referrals"
              icon="referrals"
              active={isActive('/referrals')}
              onClick={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
            >
              Referrals
            </NavLink>
          </ExpandableSection>

          <NavLink
            to="/merchant-marketplace/overview"
            icon="marketplace"
            active={location.pathname.startsWith('/merchant-marketplace')}
            onClick={() => setIsMobileMenuOpen(false)}
            isCollapsed={isSidebarCollapsed}
            badge={showMarketplaceNewBadge ? 'NEW' : undefined}
          >
            Marketplace
          </NavLink>

          {/* Content Section - Expandable */}
          <ExpandableSection
            title="Content"
            icon="pages"
            isExpanded={expandedSections.content}
            onToggle={() => toggleSection('content')}
            isCollapsed={isSidebarCollapsed}
          >
            <RoleGuard permission="media:read">
              <NavLink
                to="/media"
                icon="media"
                active={isActive('/media')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Media
              </NavLink>
            </RoleGuard>
          </ExpandableSection>

          {/* Settings Section - Expandable */}
          <ExpandableSection
            title="Settings"
            icon="settings"
            isExpanded={expandedSections.settings}
            onToggle={() => toggleSection('settings')}
            isCollapsed={isSidebarCollapsed}
          >
            <RoleGuard permission="store:view">
              <NavLink
                to="/manage-store/customization"
                icon="pages"
                active={isActive('/manage-store/customization')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Customization
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="store:view">
              <NavLink
                to="/manage-store/domain"
                icon="globe"
                active={isActive('/manage-store/domain')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Domain
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="shipping:view">
              <NavLink
                to="/shipping-zones"
                icon="shipping"
                active={isActive('/shipping-zones')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Shipping
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="team:view">
              <NavLink
                to="/team"
                icon="team"
                active={isActive('/team') && !isActive('/team/activity')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="team.management"
              >
                Team
              </NavLink>
            </RoleGuard>


            <NavLink
              to="/notifications/preferences"
              icon="bell"
              active={isActive('/notifications/preferences')}
              onClick={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              featureId="notifications.ai"
            >
              AI Notifications
            </NavLink>

            <RoleGuard permission="settings:view">
              <NavLink
                to="/settings"
                icon="settings"
                active={isActive('/settings')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                General Settings
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="settings:view">
              <NavLink
                to="/locations"
                icon="map"
                active={isActive('/locations')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
                featureId="store.locations"
              >
                Locations
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="settings:view">
              <NavLink
                to="/business-card"
                icon="businesscard"
                active={isActive('/business-card')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Business Card
              </NavLink>
            </RoleGuard>

            <RoleGuard permission="payments:view">
              <NavLink
                to="/upgrade"
                icon="upgrade"
                active={isActive('/upgrade')}
                onClick={() => setIsMobileMenuOpen(false)}
                isCollapsed={isSidebarCollapsed}
              >
                Upgrade Plan
              </NavLink>
            </RoleGuard>
          </ExpandableSection>
        </nav>

        {/* Sidebar Footer — single profile chip that opens a popover with
            Profile + Logout. No trial card, no standalone logout button. */}
        <div className={`shell-sidebar-footer ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          {user && !isSidebarCollapsed && (
            <div className="sidebar-profile-wrap" ref={profileMenuRef}>
              {/* Popover menu — opens upward above the chip when active. */}
              {profileMenuOpen && (
                <div className="sidebar-profile-menu" role="menu">
                  <Link
                    to="/profile"
                    className="sidebar-profile-menu-item"
                    role="menuitem"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <User size={16} strokeWidth={2} />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="sidebar-profile-menu-item"
                    role="menuitem"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    <Settings size={16} strokeWidth={2} />
                    <span>Settings</span>
                  </Link>
                  <div className="sidebar-profile-menu-divider" aria-hidden="true" />
                  <button
                    type="button"
                    className="sidebar-profile-menu-item sidebar-profile-menu-logout"
                    role="menuitem"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogOut size={16} strokeWidth={2} />
                    <span>Logout</span>
                  </button>
                </div>
              )}

              {/* The chip itself — tapping toggles the popover. */}
              <button
                type="button"
                className={`sidebar-profile-chip ${profileMenuOpen ? 'open' : ''}`}
                onClick={() => setProfileMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
                aria-label="Open profile menu"
              >
                <span className="sidebar-profile-avatar" aria-hidden="true">
                  {user?.profileImage ? (
                    <img src={getImageUrl(user.profileImage)} alt="" />
                  ) : (
                    <User size={16} strokeWidth={2} />
                  )}
                </span>
                <span className="sidebar-profile-body">
                  <span className="sidebar-profile-name">
                    {user.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}` : (user.username || user.email)}
                  </span>
                  <span className="sidebar-profile-meta">
                    <span className="sidebar-profile-role">{formatRoleLabel(user.merchantRole) || 'Owner'}</span>
                    {store?.name && <span className="sidebar-profile-sep">·</span>}
                    {store?.name && <span className="sidebar-profile-store">{store.name}</span>}
                  </span>
                </span>
                <ChevronRight
                  size={16}
                  className="sidebar-profile-chevron"
                  style={{
                    transform: profileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease'
                  }}
                />
              </button>
            </div>
          )}

          {/* When sidebar is collapsed (icon-only on desktop), show a small
              logout icon button so the user still has a sign-out path
              without needing to expand the chip. */}
          {isSidebarCollapsed && (
            <button
              onClick={handleLogout}
              className="logout-button collapsed"
              title="Logout"
            >
              <LogOut size={18} />
              <span className="logout-text">Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`main-content ${impersonationData ? 'has-impersonation-banner' : ''}`}>
        {/* Top bar */}
        <header className="top-header">
          <div className="header-left">
            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Location Switcher — self-contained component, fetches its own
                data and broadcasts a `locationChanged` window event when the
                merchant switches locations. Hides itself when the merchant
                has 0–1 locations. */}
            <LocationSwitcher />
          </div>

          <div className="header-right">
            <LiveToggle />

            <div className="kyc-badge-wrapper">
              <KYCBadge />
            </div>

            <QRSidebarWidget />

            <HeaderActions />

            <ProfileMenu />
          </div>
        </header>

        {/* Platform Announcement Banner */}
        {announcement && !announcementDismissed && (
          <AnnouncementBanner
            announcement={announcement}
            onDismiss={(id) => {
              // Track dismissal
              axiosInstance.post(`/merchant/announcements/${id}/dismiss`).catch(() => {});
              localStorage.setItem(`ann_dismissed_${id}`, Date.now().toString());
              setAnnouncementDismissed(true);
            }}
            onButtonClick={(id) => {
              // Track click
              axiosInstance.post(`/merchant/announcements/${id}/click`).catch(() => {});
            }}
          />
        )}

        {/* Trial Banner */}
        <TrialBanner />

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Page content */}
        {(() => {
          // Storefront chat routes split into two layout modes:
          //   - FIXED  (inbox + conversation): full-bleed, overflow hidden,
          //            internal flex columns lay out the thread + composer
          //   - SCROLL (live + settings): full-height, flush padding, but
          //            the page itself scrolls (panels grow naturally)
          const isChatConv = location.pathname === '/storefront-chat'
            || location.pathname === '/whatsapp'
            || (
              /^\/storefront-chat\/[^/]+$/.test(location.pathname)
              && !location.pathname.endsWith('/settings')
              && !location.pathname.endsWith('/live')
            );
          const isChatScroll = location.pathname === '/storefront-chat/live'
            || location.pathname === '/storefront-chat/settings';
          const isLaunchBoostPage = location.pathname === '/whatsapp/launch-boost';
          const flushTop =
            location.pathname === '/ai-analytics'
            || location.pathname === '/products/new'
            || /^\/products\/[^/]+\/edit$/.test(location.pathname)
            || isLaunchBoostPage
            || isChatConv
            || isChatScroll;
          const hideFooter =
            location.pathname === '/ai-analytics'
            || isLaunchBoostPage
            || isChatConv
            || isChatScroll;
          const fullBleed = isChatConv; // ONLY the conv layout needs overflow:hidden
          return (
            <>
              <main className={`content-area ${flushTop ? 'flush-top' : ''} ${fullBleed ? 'full-bleed' : ''} ${hideFooter ? 'no-footer' : ''}`}>
                {children}
              </main>
              {!hideFooter && (
                <footer className="merchant-footer">
                  <p className="footer-text">
                    © {new Date().getFullYear()} Revstar. All rights reserved.
                  </p>
                </footer>
              )}
            </>
          );
        })()}
      </div>

      {/* Floating AI assistant — self-gates via useFeatureAccess('ai.predictions')
          and hides itself on /ai-analytics (the page IS the chat). On mobile the
          bubble lifts above the 72px MobileBottomNav so it doesn't conflict.
          Also suppressed on every /storefront-chat* route since those pages are
          their own chat surface and the FAB overlaps the merchant composer. */}
      {!location.pathname.startsWith('/storefront-chat')
        && location.pathname !== '/whatsapp'
        && <AIChatLauncher />}

      {/* Live visitor attention-grabber — self-gates on feature access,
          self-suppresses on /storefront-chat/live and inside conversations,
          and runs its own per-visitor retry state machine. */}
      <LiveVisitorPopup />

      {/* KYC Onboarding Modal */}
      <KYCOnboardingModal />

      {/* Post-login activation carousel — shows once per browser session,
          self-skips slides where the action is already done, suppresses inside
          the mobile WebView. */}
      <PostLoginActionsModal />

      {/* Mobile bottom-tab nav — visible at <1024px only (CSS-gated).
          The Menu tab opens the existing sidebar drawer via isMobileMenuOpen.
          Hidden inside a storefront-chat conversation: the user is focused on
          replying, and the floating POS pill on the bar overlaps the
          merchant composer's send button. */}
      {!(
        location.pathname === '/storefront-chat'
        || (
          /^\/storefront-chat\/[^/]+$/.test(location.pathname)
          && !location.pathname.endsWith('/settings')
          && !location.pathname.endsWith('/live')
        )
      ) && <MobileBottomNav onMoreClick={() => setIsMobileMenuOpen(true)} />}

      {/* Onboarding Tour - disabled for testing */}
      {/* OnboardingTour removed - react-joyride causes sidebar layout issues */}
    </div>
    </ShellProvider>
  );
}
